import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Lock, User, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, verifyAdmin } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";

const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/dashboard";

  // If already logged in, redirect to dashboard
  useEffect(() => {
    (async () => {
      const ok = await verifyAdmin();
      if (ok) navigate("/admin/dashboard", { replace: true });
    })();
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Please enter both username and password.");
      return;
    }
    setLoading(true);
    try {
      // Login response sets an httpOnly session cookie server-side; we do NOT
      // store the token in localStorage (XSS hardening).
      await adminLogin(username.trim(), password);
      toast.success("Welcome back, Admin.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4">
      {/* background */}
      <div className="absolute inset-0 bg-[#0a0a0a]" />
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(1200px circle at 25% 15%, rgba(212,175,55,0.12), transparent 55%), radial-gradient(900px circle at 85% 85%, rgba(4,99,7,0.10), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8" aria-label="Seetai Properties Home">
          <img
            src="/brand/seetai-logo-full.png"
            alt="Seetai Properties"
            loading="eager"
            decoding="async"
            draggable={false}
            className="block h-14 w-auto select-none"
          />
        </Link>

        <div className="glass-strong rounded-[var(--sp-radius-lg)] p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-[color:var(--sp-gold-soft)]">
              <ShieldCheck className="h-4 w-4 text-[color:var(--sp-gold)]" />
            </span>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">Admin Access</p>
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-5 leading-tight">Sign in to your console.</h1>
          <p className="mt-2 text-white/55 text-sm">View and manage all leads captured from the Seetai website.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Username</label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
                <input
                  data-testid="admin-login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-luxe w-full pl-10 pr-4 py-3 text-sm"
                  placeholder="admin"
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Password</label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
                <input
                  data-testid="admin-login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-luxe w-full pl-10 pr-4 py-3 text-sm"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="btn-gold w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-[12px] tracking-[0.22em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="mt-7 text-[11px] text-white/35 tracking-[0.16em] uppercase text-center">
            <Link to="/" className="hover:text-[color:var(--sp-gold)] transition-colors">← Back to website</Link>
          </p>
        </div>
      </div>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "rgba(18,18,18,0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212,175,55,0.32)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
};

export default AdminLoginPage;
